/**
 * PipelineGraphView — read-only visualization of a pipeline's workflow graph.
 *
 * Presentational only. The parent passes the backend serialized graph
 * (`{ nodes, edges, nodeConfigs, stateSchema }`); this component converts it to
 * React Flow shape (with elkjs auto-layout) via the shared
 * `convertBackendGraphToReactFlow` util and renders it inside a fixed-height
 * container using the existing `GraphFlowEditor` in `readonly` mode.
 *
 * Node detail on click: handled by GraphFlowEditor itself — in `readonly` it
 * opens the existing NodeConfigModal (the Monaco-based "IDE" node modal) in
 * view-only mode (Save hidden, editors read-only). We do NOT roll our own
 * detail panel.
 *
 * Defensive: a falsy graph or one with no nodes renders a clean empty state
 * instead of crashing.
 */
import React, { useEffect, useState } from 'react';
import {
  GraphFlowEditor,
  convertBackendGraphToReactFlow,
} from '../../graph-workflow-editor';
import './PipelineGraphView.css';

const EMPTY_FLOW = { nodes: [], edges: [], nodeConfigs: {}, stateSchema: null };

const PipelineGraphView = ({ graph }) => {
  const [flow, setFlow] = useState(EMPTY_FLOW);
  const [loading, setLoading] = useState(false);

  const hasGraph = !!(graph && Array.isArray(graph.nodes) && graph.nodes.length > 0);

  // Convert backend graph → React Flow shape (async: elkjs auto-layout).
  // Guarded against out-of-order resolutions if `graph` changes mid-flight.
  useEffect(() => {
    let cancelled = false;
    if (!hasGraph) {
      setFlow(EMPTY_FLOW);
      return undefined;
    }
    setLoading(true);
    convertBackendGraphToReactFlow(graph)
      .then((result) => {
        if (!cancelled) setFlow(result || EMPTY_FLOW);
      })
      .catch(() => {
        if (!cancelled) setFlow(EMPTY_FLOW);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [graph, hasGraph]);

  if (!hasGraph) {
    return (
      <div className="pipeline-graph-view pipeline-graph-view--empty">
        <div className="pipeline-graph-view__empty-text">
          Graph not available for this pipeline.
        </div>
      </div>
    );
  }

  return (
    <div className="pipeline-graph-view">
      <div className="pipeline-graph-view__canvas">
        {loading && (
          <div className="pipeline-graph-view__loading">Laying out graph…</div>
        )}
        <GraphFlowEditor
          readonly
          syncFromProps
          initialNodes={flow.nodes}
          initialEdges={flow.edges}
          nodeConfigs={flow.nodeConfigs}
          stateSchema={flow.stateSchema}
          showResetPanel={false}
          showGraphInfoPanel={false}
        />
      </div>
    </div>
  );
};

export default PipelineGraphView;
